/**
 * pastoralTrajectoryMap.js — FASE R5.
 *
 * Maps (emotional anchor + direction) to warm, human-sounding Spanish phrases.
 * Bridging layer between analytical trajectory states and natural pastoral language.
 *
 * Rules:
 *  — No timestamps ("el 7 de abril…")
 *  — No clinical labels ("desgaste compasivo", "saturación ministerial")
 *  — No analytical framing ("el proceso indica…", "los datos muestran…")
 *  — Max 1 phrase per output — let it breathe
 *  — Use pastor-to-pastor register, not therapeutic
 *
 * Output: string | null
 */

// ── Phrase banks — indexed by anchor_direction ─────────────────────────────
// Multiple options per key so the output doesn't feel algorithmic.
// Selector uses (recurrenceCount % phrases.length) for deterministic rotation.

const PHRASES = {
  // ── Exhaustion / burnout ────────────────────────────────────────────────
  "cansancio_acumulado+recurring": [
    "Ha estado cargando peso por mucho tiempo.",
    "El cansancio de estos meses sigue presente.",
    "Hay un agotamiento que viene caminando desde atrás.",
  ],
  "cansancio_acumulado+stable_heavy": [
    "Llevas un tiempo largo cargando esto.",
    "El peso no ha aflojado.",
    "Esto ya es un cansancio de fondo.",
  ],
  "cansancio_acumulado+worsening": [
    "El cansancio parece haberse profundizado.",
    "Parece que la carga se ha hecho más pesada.",
  ],
  "cansancio_acumulado+improving": [
    "Algo del peso parece estar cediendo.",
    "La conversación anterior sonaba más pesada.",
  ],
  "cansancio_acumulado+fragile": [
    "Hay algo de alivio pero todavía es delicado.",
    "La mejoría viene pero sigue siendo frágil.",
  ],

  // ── Grief / pain ────────────────────────────────────────────────────────
  "dolor_activo+recurring": [
    "Este dolor sigue presente.",
    "El duelo todavía está vivo.",
    "Sigue en medio del dolor.",
  ],
  "dolor_activo+stable_heavy": [
    "Ha estado en este dolor por mucho tiempo.",
    "La pérdida sigue pesando.",
  ],
  "dolor_activo+worsening": [
    "El dolor parece haberse profundizado.",
    "La situación se ha hecho más difícil.",
  ],
  "dolor_activo+improving": [
    "Hay algo de movimiento desde la última vez.",
    "El dolor sigue pero hay algo de espacio.",
  ],
  "dolor_activo+fragile": [
    "La mejoría es real pero todavía es frágil.",
    "Hay pasos hacia adelante, aunque con cuidado.",
  ],
  "dolor_activo+cycling": [
    "Ha pasado por momentos de alivio y vuelve al dolor.",
    "Esta situación sigue yendo y viniendo.",
  ],

  // ── Pastoral resistance / tension ───────────────────────────────────────
  "resistencia_pastoral+recurring": [
    "Esa tensión sigue sin resolverse.",
    "La situación difícil vuelve a aparecer.",
  ],
  "resistencia_pastoral+stable_heavy": [
    "Lleva tiempo con esta tensión.",
    "Esto no se ha podido soltar.",
  ],
  "resistencia_pastoral+worsening": [
    "La tensión parece haberse intensificado.",
  ],
  "resistencia_pastoral+cycling": [
    "Esto ha ido y venido más de una vez.",
    "La tensión no se acaba de resolver.",
  ],

  // ── Spiritual searching ─────────────────────────────────────────────────
  "busqueda_espiritual+recurring": [
    "Sigue en ese tiempo de búsqueda.",
    "La búsqueda espiritual continúa.",
  ],
  "busqueda_espiritual+stable_heavy": [
    "Ha estado buscando por mucho tiempo.",
    "La búsqueda es real y sostenida.",
  ],
  "busqueda_espiritual+fragile": [
    "Hay algo que empieza a aparecer en la búsqueda.",
  ],

  // ── Spiritual confusion ─────────────────────────────────────────────────
  "confusion_espiritual+recurring": [
    "La confusión espiritual sigue presente.",
    "Sigue sin encontrar claridad.",
  ],
  "confusion_espiritual+worsening": [
    "La confusión parece haberse profundizado.",
  ],

  // ── Restoration / emerging hope ─────────────────────────────────────────
  "restauracion_emergente+improving": [
    "La restauración viene avanzando.",
    "Hay señales claras de recuperación.",
  ],
  "restauracion_emergente+fragile": [
    "La restauración avanza, aunque con pasos pequeños.",
    "Hay progreso real, aunque todavía delicado.",
  ],
  "restauracion_emergente+cycling": [
    "La restauración tiene avances y retrocesos.",
  ],

  // ── Stable growth ───────────────────────────────────────────────────────
  "crecimiento_estable+improving": [
    "El crecimiento espiritual se mantiene.",
  ],

  // ── Fallback phrases by direction (when anchor+direction pair not found) ─
  "__fallback+recurring": [
    "Esta situación sigue presente.",
    "Esto vuelve a aparecer.",
  ],
  "__fallback+stable_heavy": [
    "Ha estado cargando esto por un tiempo.",
  ],
  "__fallback+worsening": [
    "La situación parece seguir pesando.",
  ],
  "__fallback+fragile": [
    "Hay algo de mejoría, aunque todavía delicado.",
  ],
  "__fallback+cycling": [
    "Esto ha ido y venido más de una vez.",
  ],
};

/**
 * Map an anchor + direction to a warm pastoral phrase.
 *
 * @param {string|null} anchor     — from emotionalContinuityTracker
 * @param {string}      direction  — "recurring" | "worsening" | "improving" | etc.
 * @param {number}      recurrenceCount — used for phrase rotation
 * @returns {string|null}
 */
export function mapToPhrase(anchor, direction, recurrenceCount = 0) {
  if (!direction || direction === "unknown") return null;

  const specificKey  = anchor ? `${anchor}+${direction}` : null;
  const fallbackKey  = `__fallback+${direction}`;

  const phrases = (specificKey && PHRASES[specificKey]) || PHRASES[fallbackKey];

  if (!phrases?.length) return null;

  // Deterministic rotation so the output doesn't repeat identically on every call
  const phrase = phrases[recurrenceCount % phrases.length];
  return phrase ?? null;
}

/**
 * Build a trajectory fragment from a milestone note (if present).
 * Milestone notes are ONE-TIME events — phrased as warm historical acknowledgment.
 * @param {string|null} milestoneNote
 * @returns {string|null}
 */
export function mapMilestoneToPhrase(milestoneNote) {
  if (!milestoneNote) return null;

  const MILESTONE_PHRASES = {
    restauracion:        "Hubo un momento de restauración en esta historia.",
    decision_espiritual: "Tomó una decisión espiritual importante.",
    reconciliacion:      "Pasó por un proceso de reconciliación.",
    perseverancia:       "Ha mostrado perseverancia a través de cosas difíciles.",
    apertura_emocional:  "Hubo una apertura real en algún momento.",
    sanidad_relacional:  "Hay sanidad relacional de fondo.",
  };

  return MILESTONE_PHRASES[milestoneNote] ?? null;
}
